<script setup lang="ts">
import { computed } from 'vue';
import TrackCard from '@/components/TrackCard.vue';
import { useCurrentTrackRow } from '@/composables/api/useCurrentTrackRow';

/**
 * The player inspector's "Current Track" value.
 *
 * The row used to read a bare code, e.g. "DIWE5043". It now renders the same
 * compact track card the playlist Tracks section renders, so the track playing
 * right now looks like every other track in the app — cover, title, artist and
 * album, duration — instead of an identifier you have to look up.
 *
 * A code that resolves to nothing keeps showing the raw code, which is still
 * the honest answer: the player really did report that.
 */
const props = defineProps<{ row: Record<string, unknown> }>();

const trackCode = computed(() => {
    const code = props.row.player_data_current_track_code;
    return typeof code === 'string' && code.trim() !== '' ? code.trim() : null;
});

// The same check-in reports which track number of the playlist is playing.
const position = computed(() => {
    const number = props.row.player_data_playlist_track_number;
    return typeof number === 'number' && Number.isFinite(number) ? number : null;
});

const { track } = useCurrentTrackRow(trackCode, position);
</script>

<template>
    <TrackCard v-if="track" :track="track" density="compact" data-testid="lzp-current-track-card" />
    <span v-else-if="trackCode" class="text-muted-foreground font-mono text-sm">{{ trackCode }}</span>
    <span v-else class="text-muted-foreground">—</span>
</template>
